import React, { useState } from "react";
import { Avatar, Button, Input, Select, Tag, Typography, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faUserMinus,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FootballMember, FootballTeam } from "../types/Tournaments";
import { UserFavorites } from "../types/Structure";
import { AdminFootballTeams } from "../api/Tournaments";

interface FootballRosterEditorProps {
  teams: FootballTeam[];
  // Pool di utenti registrati selezionabili (favoriti del circolo).
  favorites: UserFavorites[];
  // Modalita' LIVE (default): salva subito via API e ricarica.
  onChanged?: () => void;
  // Modalita' STAGED: se fornita, le modifiche NON vengono salvate su API ma
  // notificate al parent (che le tiene in stato locale e persiste al conferma).
  onMembersChange?: (teamId: string, members: FootballMember[]) => void;
  // Rinomina squadra in modalita' STAGED (nessuna API). In modalita' live il
  // rename viene salvato subito via API.
  onNameChange?: (teamId: string, name: string) => void;
  // Se fornita, mostra il pulsante "elimina squadra" nell'header di ogni card.
  onDeleteTeam?: (team: FootballTeam) => void;
  // Se fornita, mostra un pulsante "Aggiungi squadra" in cima.
  onAddTeam?: () => void;
}

const memberLabel = (m: FootballMember) =>
  `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim();

const FootballRosterEditor: React.FC<FootballRosterEditorProps> = ({
  teams,
  favorites,
  onChanged,
  onMembersChange,
  onNameChange,
  onDeleteTeam,
  onAddTeam,
}) => {
  const [savingTeamId, setSavingTeamId] = useState<string | null>(null);
  const [guestInputs, setGuestInputs] = useState<Record<string, string>>({});

  const save = async (team: FootballTeam, members: FootballMember[]) => {
    // Staged: notifico il parent, nessuna chiamata API.
    if (onMembersChange) {
      onMembersChange(team.id, members);
      return;
    }
    // Live: salvo subito e ricarico.
    setSavingTeamId(team.id);
    try {
      await AdminFootballTeams.updateMembers(team.id, members);
      onChanged?.();
    } catch {
      message.error("Errore nel salvataggio della rosa");
    } finally {
      setSavingTeamId(null);
    }
  };

  // Se posso gestire il rename: staged (onNameChange) oppure live (no staging).
  const canRename = !!onNameChange || !onMembersChange;

  const saveName = async (team: FootballTeam, rawName: string) => {
    const name = rawName.trim();
    if (!name || name === team.name) return;
    // Staged: notifico il parent, nessuna API.
    if (onNameChange) {
      onNameChange(team.id, name);
      return;
    }
    // Live: salvo subito e ricarico.
    setSavingTeamId(team.id);
    try {
      await AdminFootballTeams.renameTeam(team.id, name);
      onChanged?.();
    } catch {
      message.error("Errore nella modifica del nome squadra");
    } finally {
      setSavingTeamId(null);
    }
  };

  const addUser = (team: FootballTeam, userId: string) => {
    const fav = favorites.find((f) => f.userId === userId);
    if (!fav) return;
    if ((team.members || []).some((m) => m.userId === userId)) return;
    const newMember: FootballMember = {
      userId,
      firstName: fav.user.name,
      lastName: fav.user.familyName,
      avatarUrl: fav.user.photo,
      guest: false,
    };
    save(team, [...(team.members || []), newMember]);
  };

  const addGuest = (team: FootballTeam) => {
    const raw = (guestInputs[team.id] || "").trim();
    if (raw.length < 2) return;
    const [first, ...rest] = raw.split(/\s+/);
    const newMember: FootballMember = {
      firstName: first,
      lastName: rest.join(" "),
      guest: true,
    };
    setGuestInputs((prev) => ({ ...prev, [team.id]: "" }));
    save(team, [...(team.members || []), newMember]);
  };

  const removeAt = (team: FootballTeam, index: number) => {
    save(
      team,
      (team.members || []).filter((_, i) => i !== index)
    );
  };

  return (
    <div>
      {onAddTeam && (
        <Button
          type="dashed"
          block
          onClick={onAddTeam}
          icon={<FontAwesomeIcon icon={faUserPlus} />}
          style={{ marginBottom: 12 }}
        >
          Aggiungi squadra
        </Button>
      )}
      {teams.map((team) => {
        const memberIds = (team.members || [])
          .map((m) => m.userId)
          .filter(Boolean) as string[];
        const options = favorites
          .filter((f) => !memberIds.includes(f.userId))
          .map((f) => ({
            label: `${f.user.name} ${f.user.familyName}`,
            value: f.userId,
          }));
        const saving = savingTeamId === team.id;
        const guest = guestInputs[team.id] || "";

        return (
          <div
            key={team.id}
            style={{
              border: "1px solid #eee",
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
              background: "#fbfbfd",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <Typography.Text
                strong
                style={{ flex: 1, marginRight: 8 }}
                editable={
                  canRename && !saving
                    ? {
                        tooltip: "Modifica nome",
                        onChange: (v) => saveName(team, v),
                        maxLength: 40,
                      }
                    : false
                }
              >
                {team.name}
              </Typography.Text>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {(team.members || []).length}{" "}
                  {(team.members || []).length === 1 ? "giocatore" : "giocatori"}
                </span>
                {onDeleteTeam && (
                  <Button
                    danger
                    size="small"
                    disabled={saving}
                    onClick={() => onDeleteTeam(team)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                )}
              </div>
            </div>

            {(team.members || []).length === 0 ? (
              <small className="text-muted">Nessun giocatore</small>
            ) : (
              (team.members || []).map((m, i) => (
                <div
                  key={`${m.userId ?? "guest"}-${i}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 0",
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  <Avatar size={28} src={m.avatarUrl || undefined}>
                    {(memberLabel(m)[0] || "?").toUpperCase()}
                  </Avatar>
                  <span style={{ flex: 1 }}>{memberLabel(m)}</span>
                  {m.guest && <Tag color="purple">Ospite</Tag>}
                  <Button
                    danger
                    size="small"
                    type="text"
                    disabled={saving}
                    onClick={() => removeAt(team, i)}
                    icon={<FontAwesomeIcon icon={faUserMinus} />}
                  />
                </div>
              ))
            )}

            {/* Aggiungi utente registrato (dai favoriti del circolo) */}
            <div style={{ marginTop: 10 }}>
              <Select
                showSearch
                placeholder="Aggiungi utente GetMove"
                style={{ width: "100%" }}
                value={null as unknown as string}
                options={options}
                optionFilterProp="label"
                disabled={saving}
                onChange={(v) => addUser(team, v as unknown as string)}
                notFoundContent="Nessun utente tra i favoriti"
              />
            </div>

            {/* Aggiungi ospite (non registrato) */}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Input
                placeholder="Nome ospite"
                value={guest}
                disabled={saving}
                onChange={(e) =>
                  setGuestInputs((prev) => ({
                    ...prev,
                    [team.id]: e.target.value,
                  }))
                }
                onPressEnter={() => addGuest(team)}
              />
              <Button
                type="primary"
                disabled={saving || guest.trim().length < 2}
                onClick={() => addGuest(team)}
                icon={<FontAwesomeIcon icon={faUserPlus} />}
              >
                Ospite
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FootballRosterEditor;
